import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Teacher, Bookmark } from "iconsax-react";
import styled from "styled-components";
import { useAuth } from "../../../hooks/auth";
import geminiService from "../../../services/geminiService";
import MobileLayout from "../../../components/MobileLayout";
import {
  MessageList,
  MessageBubble,
  InputArea,
  DactylologyContainer,
  LoadingDots,
} from "./styles";

const BotAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
`;

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

const DATILOLOGIA_REGEX = /\[DATILOLOGIA:\s*([^\]]+)\]/i;
const PALAVRA_DATILOLOGIA_REGEX = /\[PALAVRA_DATILOLOGIA:\s*([^\]]+)\]/i;

const TutorIA: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const messageListRef = useRef<HTMLDivElement>(null);

  const loadHistory = useCallback(async () => {
    if (!user?.uid) return;
    const history = await geminiService.getChatHistory(user.uid);
    if (history.length > 0) {
      setMessages(history as Message[]);
    } else {
      // Welcome message
      setMessages([
        {
          id: "welcome",
          role: "assistant",
          content: `Olá **${user.name}**! Eu sou o seu Tutor de Libras. Como posso te ajudar hoje? Posso te ensinar novos sinais, explicar a gramática da Libras ou falar sobre a cultura surda.`,
          timestamp: Date.now(),
        },
      ]);
    }
  }, [user]);

  const scrollToBottom = useCallback(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    if (user?.uid) {
      loadHistory();
    }
  }, [user, loadHistory]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  const handleSend = async () => {
    if (!inputText.trim() || loading || !user?.uid) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: inputText,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    setLoading(true);

    try {
      const response = await geminiService.sendMessageToTutor(
        user.uid,
        userMessage.content,
        messages
      );

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: response,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
    } finally {
      setLoading(false);
    }
  };

  const parseBoldSegments = (text: string) => {
    const parts = text.split(/\*\*/);
    return parts.map((part, i) => (
      <span
        /* eslint-disable-next-line react/no-array-index-key */
        key={`bold-${i}`}
        style={{ fontWeight: i % 2 === 1 ? "bold" : "normal" }}
      >
        {part}
      </span>
    ));
  };

  const parseDactylology = (text: string) => {
    const match =
      DATILOLOGIA_REGEX.exec(text) || PALAVRA_DATILOLOGIA_REGEX.exec(text);
    const word = match ? match[1].trim() : null;
    const displayText = word
      ? text
          .replaceAll(/\[(DATILOLOGIA|PALAVRA_DATILOLOGIA):\s*[^\]]+\]/gi, "")
          .trim()
      : text;

    return { displayText, word };
  };

  const renderMessageContent = (message: Message) => {
    if (message.role === "user") {
      return <div>{message.content}</div>;
    }

    const { displayText, word } = parseDactylology(message.content);
    const letters = word ? word.toUpperCase().split("") : [];

    return (
      <div>
        <BotAvatar>
          <Teacher size="18" variant="Bold" />
        </BotAvatar>
        <div>{parseBoldSegments(displayText)}</div>
        {word && (
          <DactylologyContainer>
            <p>Soletração em datilologia:</p>
            <div className="letters">
              {letters.map((letter, i) => (
                <span
                  /* eslint-disable-next-line react/no-array-index-key */
                  key={`letter-${i}`}
                >
                  {letter}
                </span>
              ))}
            </div>
            <button
              type="button"
              onClick={() => navigate("/dactylology", { state: { word } })}
            >
              <Bookmark size="18" variant="Bold" />
              Ver animação completa
            </button>
          </DactylologyContainer>
        )}
      </div>
    );
  };

  return (
    <MobileLayout headerTitle="Tutor IA" headerType="private">
      <div
        style={{
          height: "calc(100vh - 120px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MessageList ref={messageListRef}>
          {messages.map((msg) => (
            <MessageBubble key={msg.id} isUser={msg.role === "user"}>
              {renderMessageContent(msg)}
              <div className="timestamp">
                {new Date(msg.timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </MessageBubble>
          ))}
          {loading && (
            <MessageBubble isUser={false}>
              <BotAvatar>
                <Teacher size="18" variant="Bold" />
              </BotAvatar>
              <LoadingDots>
                <span />
                <span />
                <span />
              </LoadingDots>
            </MessageBubble>
          )}
        </MessageList>

        <InputArea>
          <textarea
            placeholder="Digite sua dúvida sobre Libras..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
          />
          <button
            type="button"
            className="send-button"
            onClick={handleSend}
            disabled={!inputText.trim() || loading}
            aria-label="Enviar mensagem"
          >
            <Send size="24" variant="Bold" />
          </button>
        </InputArea>
      </div>
    </MobileLayout>
  );
};

export default TutorIA;
